import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLang } from "../i18n/LanguageContext";
import { EImg } from "./Editable";
import { useShopSettings, isSectionOff, PATH_SECTION } from "../lib/shopSettings";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const NAV_LINKS = [
  { to: "/", testid: "nav-home", key: "nav.home" },
  { to: "/boutique", testid: "nav-catalogue", key: "nav.catalogue" },
  { to: "/forfaits", testid: "nav-forfaits", key: "nav.forfaits" },
  { to: "/catalogues", testid: "nav-catalogues", key: "nav.catalogues" },
  { to: "/plaquettes-sur-mesure", testid: "nav-plaquettes", key: "nav.plaquettes" },
  { to: "/blogue", testid: "nav-blog", key: "nav.blog" },
  { to: "/realisations", testid: "nav-realisations", key: "nav.realisations" },
  { to: "/a-propos", testid: "nav-about", key: "nav.about" },
  { to: "/faq", testid: "nav-faq", key: "nav.faq" },
  { to: "/contact", testid: "nav-contact", key: "nav.contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { lang, setLang, t } = useLang();
  const settings = useShopSettings();
  const links = NAV_LINKS.filter((l) => !PATH_SECTION[l.to] || !isSectionOff(settings, PATH_SECTION[l.to]));
  // PAGE DE SPORT EN VEDETTE selon le CALENDRIER (demande du propriétaire,
  // 2026-10-27 : « la page hockey en vedette dans le menu pendant la saison ») :
  // saison de hockey = septembre à avril — le lien doré disparaît hors saison
  const saisonHockey = (() => { const m = new Date().getMonth(); return m >= 8 || m <= 3; })();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  // Bascule FR/EN avec URLs DÉDIÉES (demande du propriétaire, 2026-10-06 — SEO
  // anglophone) : EN navigue vers /en/… (URL partageable et indexable), FR la retire
  const switchLang = (l) => {
    setLang(l);
    const isEn = location.pathname === "/en" || location.pathname.startsWith("/en/");
    if (l === "en" && !isEn) navigate((location.pathname === "/" ? "/en" : `/en${location.pathname}`) + location.search);
    if (l === "fr" && isEn) navigate((location.pathname.slice(3) || "/") + location.search);
  };

  // précision du préfixe : /catalogues et /catalogue-interactif ne doivent PAS activer « La Boutique »
  const isActive = (to) =>
    location.pathname === to
    || location.pathname.startsWith(to + "/")
    || (to === "/catalogues" && ["/catalogue-viewer", "/catalogue-interactif"].includes(location.pathname));

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b border-gold/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-4 xl:px-10 h-16 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
            {/* Nouveau logo 100 % or (créé 2026-09-18) : composition fidèle à l'original,
                chemise qui s'ouvre à côté du bonhomme (pas sur le T) — posé directement sur
                l'en-tête sombre, sans badge. Jamais déformé : hauteur fixe + largeur auto, ×1,7 */}
            <EImg editPath="img.nav_logo" src={lang === "en" ? "/logo-gold-t-en.png" : "/logo-gold-t.png"} alt="Mr Trophée"
              className="h-12 w-auto shrink-0 transition-transform duration-500 group-hover:scale-105 drop-shadow-[0_2px_10px_rgba(212,175,55,0.35)]" />
          </Link>

          {/* HAMBURGER PARTOUT, même sur PC (demande du propriétaire, 2026-10-27 :
              « trop de matériel — garder le hamburger actif même en PC ») : plus
              de rangée de liens sur grand écran ; SEULE la pilule EN VEDETTE
              reste visible hors du menu (saison sept. → avr.) ; les autres pages
              sont toutes dans le menu hamburger.
              2026-10-28 (demande du propriétaire) : pilule COLLÉE À GAUCHE, à
              côté du logo, et renommée « Pour vos tournois hockey » */}
          {saisonHockey && (
            <Link to="/trophees-hockey" data-testid="nav-hockey"
              className={`hidden sm:inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs whitespace-nowrap shrink-0 transition-all duration-300 ${
                isActive("/trophees-hockey")
                  ? "border-gold bg-gold text-ink font-bold"
                  : "border-gold/50 text-gold hover:bg-gold hover:text-ink"
              }`}>
              {lang === "en" ? "For your hockey tournaments" : "Pour vos tournois hockey"}
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="flex items-center rounded-full border border-ivory/15 overflow-hidden" data-testid="lang-toggle">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => switchLang(l)}
                className={`px-2 sm:px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                  lang === l ? "bg-gold text-ink" : "text-ivory/60 hover:text-gold"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <Link
            to="/panier"
            data-testid="nav-cart"
            className="relative p-2 text-ivory/80 hover:text-gold transition-colors duration-300"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-gold text-ink text-[10px] font-bold min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center px-1">
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-user-menu" className="flex items-center gap-2 text-sm text-ivory/80 hover:text-gold transition-colors duration-300 outline-none">
                {user.picture ? (
                  <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-7 h-7 rounded-full border border-gold/40 object-cover" />
                ) : (
                  <User className="w-5 h-5" />
                )}
                <span className="hidden xl:inline max-w-[120px] truncate">{user.name}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-surface border-gold/20 text-ivory">
                <DropdownMenuItem data-testid="nav-account" onClick={() => navigate("/compte")} className="cursor-pointer focus:bg-gold/10">
                  <User className="w-4 h-4 mr-2" /> {t("nav.account")}
                </DropdownMenuItem>
                {(user.role === "admin" || user.role === "vendeur") && (
                  <DropdownMenuItem data-testid="nav-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-gold/10">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                {(user.role === "admin" || user.role === "vendeur") && (
                  <DropdownMenuItem data-testid="nav-vendeur" onClick={() => navigate("/vendeur")} className="cursor-pointer focus:bg-gold/10">
                    <User className="w-4 h-4 mr-2" /> Portail vendeur
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer focus:bg-gold/10">
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/connexion"
              data-testid="nav-login"
              className="text-sm px-2.5 sm:px-5 py-2 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors duration-300 inline-flex items-center"
            >
              <User className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline">{t("nav.login")}</span>
            </Link>
          )}
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            className="p-2 text-ivory/80 hover:text-gold transition-colors duration-300"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Menu hamburger à TOUTES les tailles, avec AJUSTEMENT AUTOMATIQUE à
          l'écran (demande du propriétaire, 2026-10-27 : « mettre un ajustement
          automatique pour chaque écran ») : pleine largeur sur téléphone,
          panneau net de largeur fixe aligné à droite sur tablette et PC */}
      {menuOpen && (
        <nav data-testid="nav-mobile-menu" data-lenis-prevent
          className="border-t border-gold/10 bg-black/90 backdrop-blur-xl max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
          <div className="max-w-7xl mx-auto px-6 pt-3 pb-28 sm:pb-8 flex flex-col text-sm tracking-wide sm:items-end">
            <div className="flex flex-col w-full sm:w-72">
              {links.map((l) => (
                <Link
                  key={l.to} to={l.to} data-testid={`mobile-${l.testid}`}
                  className={`py-3.5 border-b border-ivory/5 last:border-0 transition-colors duration-300 ${
                    isActive(l.to) ? "text-gold font-bold" : "text-ivory/80 hover:text-gold"
                  }`}
                >
                  {t(l.key)}
                </Link>
              ))}
              {/* Hockey en vedette (sept. → avr.) : sur petit écran il vit dans
                  le menu — à partir de sm il est déjà visible dans la barre */}
              {saisonHockey && (
                <Link to="/trophees-hockey" data-testid="mobile-nav-hockey"
                  className={`sm:hidden py-3.5 transition-colors duration-300 ${
                    isActive("/trophees-hockey") ? "text-gold font-bold" : "text-gold/90 hover:text-gold"
                  }`}>
                  {lang === "en" ? "★ For your hockey tournaments" : "★ Pour vos tournois hockey"}
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
